import { Database } from '@databases/types/database';
import { DatabaseViewSettings } from '@databases/types/database/view';
import {
  DatabaseChangeset,
  DatabaseDraft,
  getDatabaseDraft,
} from '@databases/utils/database/changesets';
import { applyDatabaseView } from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { omit } from 'lodash-es';
import { createCachedSelector } from 're-reselect';

import type { DefaultRootState } from '../redux';

export const emptyArr = Object.freeze([]) as never[];

/**
 * Returns true if there are any running save operations for the given database.
 * @param scope If defined, only returns the save operations for the given scope. If undefined, returns all save operations for all scopes.
 */
export function selectHasActiveSaveOperation(
  state: DefaultRootState,
  {
    contentId,
    scope,
  }: {
    contentId: string;
    scope?: string;
  }
): boolean {
  if (scope) {
    return (state.changesets.scopes[scope]?.activeSaveOperations[contentId] ?? 0) > 0;
  } else {
    return (state.changesets.activeSaveOperations[contentId] ?? 0) > 0;
  }
}

/**
 * Returns the current drafted changeset for the specified database.
 */
export const selectDraftedChangeset = createCachedSelector<
  DefaultRootState,
  {
    contentId: string;
    scope: string;
  },
  string,
  DatabaseChangeset,
  DatabaseChangeset,
  DatabaseChangeset
>(
  (state, { contentId }) => contentId,
  (state, { contentId, scope }) =>
    state.changesets.scopes[scope]?.savingChangeset[contentId] ?? emptyArr,
  (state, { contentId, scope }) =>
    state.changesets.scopes[scope]?.draftedChangesets[contentId] ?? emptyArr,
  (contentId, saving, drafted) => {
    if (!contentId) {
      return emptyArr;
    }

    return [...saving, ...drafted];
  }
)((state, { contentId, scope }) => `${contentId}-${scope}`);

const selectDatabaseDraftByContentIdInternal = createCachedSelector<
  DefaultRootState,
  {
    contentId: string;
    scope: string;
    viewSettings?: DatabaseViewSettings;
    ignoredEntries?: string[];
  },
  Database,
  DatabaseChangeset,
  DatabaseViewSettings | undefined,
  string[] | undefined,
  DefaultRootState,
  DatabaseDraft
>(
  selectDatabaseByContentIdRequired,
  selectDraftedChangeset,
  (state, { viewSettings }) => viewSettings,
  (state, { ignoredEntries }) => ignoredEntries,
  (state) => state,
  (database, changeset, viewSettings, ignoredEntries, state) => {
    const databaseDraft = getDatabaseDraft(database, changeset);
    return viewSettings
      ? applyDatabaseView(database, databaseDraft, viewSettings, state, ignoredEntries)
      : databaseDraft;
  }
)(
  (state, { contentId, scope, viewSettings }) => `${contentId}-${scope}-${!!viewSettings}`
);

export const selectDatabaseDraftByContentId = createCachedSelector<
  DefaultRootState,
  {
    contentId: string;
    scope: string;
    /**
     * If true, deleted fields/entries will be present in the draft (marked as deleted in the "deletedFields" and
     * "deletedEntries" properties.
     * If false, deleted fields/entries will not be present in the draft.
     *
     * This property is not optional as it is a common source of error. Components that receive a fieldId/entryId
     * as a prop and use that to load the field/entry from the database should almost always set this to true,
     * as many parts of the UI keep showing deleted fields/entries until the draft is saved.
     * Setting this to false in combination with using getDatabaseFieldRequired/getDatabaseEntryRequired will usually
     * lead to exceptions and component crashes, so in such cases usually getDatabaseFieldOptional/getDatabaseEntryOptional
     * need to be used.
     */
    includeDeleted: boolean;
    viewSettings?: DatabaseViewSettings;
    // entries ignored by the view, used for new entries that should not have sort applied
    ignoredEntries?: string[];
  },
  DatabaseDraft,
  boolean,
  DatabaseDraft
>(
  selectDatabaseDraftByContentIdInternal,
  (state, { includeDeleted }) => includeDeleted,
  (databaseDraft, includeDeleted) => {
    if (includeDeleted) {
      return databaseDraft;
    } else {
      return {
        ...databaseDraft,
        fields: databaseDraft.fields.filter(
          (fieldId) => !databaseDraft.deletedFields.includes(fieldId)
        ),
        fieldsById: omit(databaseDraft.fieldsById, databaseDraft.deletedFields),
        entries: databaseDraft.entries.filter(
          (entryId) => !databaseDraft.deletedEntries.includes(entryId)
        ),
        entriesById: omit(databaseDraft.entriesById, databaseDraft.deletedEntries),
      };
    }
  }
)(
  (state, { contentId, scope, viewSettings, includeDeleted: includeDeleted }) =>
    `${contentId}-${scope}-${!!viewSettings}-${includeDeleted}`
);
